import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'ES' | 'EN';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => any;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>('ES');

  // Simple translation helper
  const t = (key: string) => {
    const keys = key.split('.');
    let result: any = translations[language];
    for (const k of keys) {
      if (result && result[k]) {
        result = result[k];
      } else {
        return key;
      }
    }
    return result;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translations will be imported or defined here
const translations: Record<Language, any> = {
  ES: {
    nav: {
      home: 'Inicio',
      about: 'Sobre mí',
      skills: 'Habilidades',
      experience: 'Experiencia',
      portfolio: 'Portafolio',
      contact: 'Contacto',
    },
    hero: {
      greeting: 'Hola, soy',
      name: 'Cristobal Bello Sanchez',
      title: 'Desarrollador Web',
      subtitle: 'Especialista en automatización y soluciones digitales.',
      cta_portfolio: 'Ver Portafolio',
      cta_contact: 'Contactar',
    },
    about: {
      title: 'Sobre mí',
      description: 'Desarrollador web con amplia experiencia en la creación de soluciones digitales, automatización de procesos y desarrollo de plataformas web utilizando múltiples tecnologías y CMS.',
      location: 'Santo Domingo Este, República Dominicana',
      age: '34 años',
      nationality: 'Dominicana',
      profesion: 'Ingeniero en Sistemas y Computación',
      details: {
        location_label: 'Ubicación',
        age_label: 'Edad',
        nationality_label: 'Nacionalidad',
        profesion_label: 'profesión',
      }
    },
    skills: {
      title: 'Habilidades',
      tech_title: 'Tecnologías',
      cms_title: 'Experiencia en CMS',
      wp_skills_title: 'Habilidades en General',
      multimedia_title: 'Multimedia & Diseño',
      wp_skills: [
        'Desarrollo de plugins',
        'Customización de temas',
        'WooCommerce',
        'Optimización SEO',
        'Optimización Responsive',
        'Scripts a la Medida',
      ]
    },
    experience: {
      title: 'Experiencia Laboral',
      items: [
        {
          company: 'Rodriguez Santos Soluciones Web',
          period: '2023 – 2026',
          description: [
            'Desarrollo de diversos tipos de páginas web para múltiples empresas según la necesidad del cliente, utilizando herramientas como: WordPress, Shopify y Wix.',
            'Customización y desarrollo de plugins para WordPress.',
            'Edición de contenido multimedia (imágenes y videos) para la web.'
          ]
        },
        {
          company: 'LORD COMPUTER INC',
          period: '2014 – 2022',
          description: [
            'Desarrollo de scripts personalizados para resolución de problemas y automatizaciones de procesos que ayudaron con el flujo del trabajo. (PHP, Javascript, Google Apps Scripts, APIs).', 
            'Mantenimiento y soporte en servidor web local de tienda en CMS Magento.'
          ]
        },
        {
          company: 'MediaExperto',
          period: '2011 – 2025',
          description: [
            'Desarrollo de diversos tipos de páginas web para múltiples empresas según la necesidad del cliente, utilizando herramientas como: WordPress, Shopify y Wix.',
            'Customización y desarrollo de plugins para WordPress.',
            'Edición de contenido multimedia (imágenes y videos) para la web.'
          ]        
        } 
      ]
    },
    portfolio: {
      title: 'Portafolio',
      view_project: 'Ver Proyecto',
      more_info: 'Más Información',
      tools_used: 'Herramientas utilizadas',
      category: 'Categoría',
      show_more: 'Ver todos',
      show_less: 'Ocultar',
      categories: {
        websites: 'Sitios Web',
        apps: 'Aplicaciones',
        scripts: 'Scripts',
      }
    },
    education: {
      title: 'Educación',
      degree: 'Ingeniería de Sistemas y Computación',
      period: '2009 – 2013',
      courses_title: 'Cursos & Certificaciones',
      courses: [
        'Fundamentos de ITIL V.3, Seguridad Informática y Criptográfica, Aseguramiento de Calidad de Software - Universidad Dominicana O&M',
        'Instituto Cultural Dominico Americano – Inglés de Inmersión',
        'Escuela Virtual Expression – Desarrollo Web',
        'Escuela Virtual CesarCancino – PHP Orientado a Objetos',
      ]
    },
    languages: {
      title: 'Idiomas',
      items: [
        { name: 'Español', level: 'Nativo' },
        { name: 'Inglés', level: 'Intermedio B1/B2' },
      ]
    },
    contact: {
      title: 'Contacto',
      subtitle: '¿Tienes un proyecto en mente? ¡Hablemos!',
      send: 'Enviar Mensaje',
      name_placeholder: 'Tu Nombre',
      email_placeholder: 'Tu Email',
      message_placeholder: 'Tu Mensaje',
      success_message: '¡Mensaje enviado con éxito!',
      error_message: 'Hubo un error al enviar el mensaje. Por favor, inténtalo de nuevo.',
    },
    footer: {
      rights: 'Todos los derechos reservados.',
      back_to_top: 'Volver arriba',
    }
  },
  EN: {
    nav: {
      home: 'Home',
      about: 'About',
      skills: 'Skills',
      experience: 'Experience',
      portfolio: 'Portfolio',
      contact: 'Contact',
    },
    hero: {
      greeting: "Hi, I'm",
      name: 'Cristobal Bello Sanchez',
      title: 'Web Developer',
      subtitle: 'Specialized in automation and digital solutions.',
      cta_portfolio: 'View Portfolio',
      cta_contact: 'Contact Me',
    },
    about: {
      title: 'About Me',
      description: 'Web developer with extensive experience in creating digital solutions, process automation, and web platform development using multiple technologies and CMS.',
      location: 'Santo Domingo Este, Dominican Republic',
      age: '34 years old',
      nationality: 'Dominican',
      profesion: 'Systems and Computer Engineer',
      details: {
        location_label: 'Location',
        age_label: 'Age',
        nationality_label: 'Nationality',
        profesion_label: 'profession',
      }
    },
    skills: {
      title: 'Skills',
      tech_title: 'Technologies',
      cms_title: 'CMS Experience',
      wp_skills_title: 'General Skills',
      multimedia_title: 'Multimedia & Design',
      wp_skills: [
        'Plugin Development',
        'Theme Customization',
        'WooCommerce',
        'SEO Optimization',
        'Responsive Optimization',
        'Custom Scripts',
      ]
    },
    experience: {
      title: 'Work Experience',
      items: [
        {
          company: 'Rodriguez Santos Soluciones Web',
          period: '2023 – 2026',
          description: [
            'Development of various types of websites for multiple companies according to client needs, using tools such as: WordPress, Shopify, and Wix.',
            'Customization and development of WordPress plugins.',
            'Editing multimedia content (images and videos) for the web.'
          ]
        },
        {
          company: 'LORD COMPUTER INC',
          period: '2014 – 2022',
          description: [
            'Development of custom scripts for problem-solving and process automation that improved workflow. (PHP, Javascript, Google Apps Scripts, APIs).',
            'Maintenance and support for local web server of Magento CMS store.'
          ]
        },
        {
          company: 'MediaExperto',
          period: '2011 – 2025',
          description: [
            'Development of various types of websites for multiple companies according to client needs, using tools such as: WordPress, Shopify, and Wix.',
            'Customization and development of WordPress plugins.',
            'Editing multimedia content (images and videos) for the web.'
          ]        
        }
      ]
    },
    portfolio: {
      title: 'Portfolio',
      view_project: 'View Project',
      more_info: 'More Info',
      tools_used: 'Tools used',
      category: 'Category',
      show_more: 'Show all',
      show_less: 'Hide',
      categories: {
        websites: 'Websites',
        apps: 'Applications',
        scripts: 'Scripts',
      }
    },
    education: {
      title: 'Education',
      degree: 'Systems and Computer Engineering',
      period: '2009 – 2013',
      courses_title: 'Courses & Certifications',
      courses: [
        'ITIL V.3 Foundations, Information and Cryptographic Security, Software Quality Assurance - Universidad Dominicana O&M',
        'Dominican American Cultural Institute – Immersion English',
        'Virtual Expression School – Web Development',
        'CesarCancino Virtual School – Object-Oriented PHP',
      ]
    },
    languages: {
      title: 'Languages',
      items: [
        { name: 'Spanish', level: 'Native' },
        { name: 'English', level: 'Intermediate B1/B2' },
      ]
    },
    contact: {
      title: 'Contact',
      subtitle: 'Have a project in mind? Let\'s talk!',
      send: 'Send Message',
      name_placeholder: 'Your Name',
      email_placeholder: 'Your Email',
      message_placeholder: 'Your Message',
      success_message: 'Message sent successfully!',
      error_message: 'There was an error sending the message. Please try again.',
    },
    footer: {
      rights: 'All rights reserved.',
      back_to_top: 'Back to top',
    }
  }
};
